import { ChangeEvent, ReactNode } from "react";
import Box from "@mui/material/Box";
import GridViewIcon from "@mui/icons-material/GridView";
import SearchIcon from "@mui/icons-material/Search";
import ViewListIcon from "@mui/icons-material/ViewList";
import InputBase from "@mui/material/InputBase";
import type { PageSize, ViewMode } from "./useConfigList";

/**
 * SIMS-8594 — the config-list toolbar.
 *
 * Presentational: every control reports upward and nothing is stored here, so the shell stays
 * the single source of truth and a view switch cannot lose state (AC1, TestRail C17595).
 *
 * Spec — `Payers Pattern v2.dc.html`:
 *   segmented toggle L95-103: --surface-2 track, 8px radius, 4px padding; buttons 32px,
 *                             13px/600, 6px radius, `view_list` / `grid_view`
 *   search           L116-117: 40px, 1px --border-default, 8px radius, 380px,
 *                             placeholder "Search payers by name, description or pattern"
 *   status chips     L119-122: pills (999px), 32px, 13px/500 — All / Active / Inactive.
 *                             Chips, NOT a select; the category select is a separate control.
 * Rows-per-page follows `Roles Pattern v2.dc.html:107-111` (10/25/50/All); FR-10 places it next
 * to the view toggle "so it governs both views". The Payers export has no such control at all —
 * recorded as a deviation in the story's source manifest.
 *
 * Accessibility beyond the mock: the toggle and chips carry `aria-pressed`, the search input has
 * a real accessible name, and the page-size select is labelled. The prototype conveys selection
 * with colour alone, which NFR-5 forbids.
 */

export interface StatusOption {
	value: string;
	label: string;
}

/** View + pagination controls, rendered on the page header row by ConfigListViewControls. */
export interface ConfigListViewControlsProps {
	/**
	 * Extra segments rendered inside the view track, after Table and Cards.
	 *
	 * The Actions screen's Groups tab puts "Roles preview" here, as the design does — it is
	 * another way of looking at the same records, so it belongs with the other two rather
	 * than beside them as a separate button. Optional, and every other list passes nothing.
	 */
	extraControls?: ReactNode;
	/**
	 * The segment currently showing. Typed loosely on purpose: a screen may add segments
	 * through `extraControls`, and when one of those is active NEITHER Table nor Cards
	 * should read as selected — which is what the design does on the Groups tab's preview.
	 * An unrecognised value simply highlights nothing.
	 */
	view: ViewMode | string;
	onViewChange: (v: ViewMode) => void;
	pageSize: PageSize;
	onPageSizeChange: (s: PageSize) => void;
	pageSizeOptions: readonly string[];
	/**
	 * Design v2 (2026-08-28 re-issue) moved rows-per-page into the pager at the foot of the
	 * frame, leaving only the view toggle on the header row. Kept optional rather than removed:
	 * Actions and Cups & Analytes have not been re-issued yet, so they may still want it here.
	 */
	showPageSize?: boolean;
}

/** One filter pill — the status chips and the "chips" category variant share it (L119-122). */
const FilterChip = ({
	label,
	active,
	onClick,
}: {
	label: string;
	active: boolean;
	onClick: () => void;
}) => (
	<Box
		component="button"
		type="button"
		aria-pressed={active}
		onClick={onClick}
		sx={{
			height: "2rem",
			padding: "0 0.875rem",
			borderRadius: "999px",
			fontSize: "0.8125rem",
			fontWeight: 500,
			cursor: "pointer",
			border: "1px solid",
			borderColor: active ? "primary.main" : "divider",
			backgroundColor: active
				? (theme) => theme.palette.surfaceTint.main
				: "background.paper",
			color: active ? "primary.dark" : "text.secondary",
		}}
	>
		{label}
	</Box>
);

/** Search + filters row. */
export interface ConfigListToolbarProps {
	query: string;
	onQueryChange: (q: string) => void;
	searchPlaceholder: string;
	status: string;
	onStatusChange: (s: string) => void;
	/** Empty for a screen with no status concept. */
	statusOptions: StatusOption[];
	/**
	 * SIMS-8634 AC3 — the category filter (design L124-132): a labelled select whose first option
	 * is "All categories". Omit for a screen with no categories.
	 */
	categoryLabel?: string;
	category?: string;
	onCategoryChange?: (c: string) => void;
	categoryOptions?: { value: string; label: string }[];
	/**
	 * How the category filter renders. "select" (the default) is Payers' labelled select. "chips"
	 * is the pill row the Cups & Analytes Hi-Fi draws instead (L134-136, SIMS-9253 / SIMS-9256).
	 */
	categoryVariant?: "select" | "chips";
	/** The first chip's label in "chips" mode, e.g. "All vendors". */
	allCategoriesLabel?: string;
	/** Any further filters. */
	filters?: ReactNode;
}

// SVG icon components, NOT MUI's <Icon> ligature element: this app never loads the Material
// Symbols font, so <Icon> renders its ligature text as raw characters ("vii Table", "se" in the
// search field). 115 other files in this repo use @mui/icons-material for exactly that reason.
const VIEWS: {
	value: ViewMode;
	label: string;
	Icon: typeof ViewListIcon;
}[] = [
	{ value: "table", label: "Table", Icon: ViewListIcon },
	{ value: "cards", label: "Cards", Icon: GridViewIcon },
];

/**
 * The view toggle and rows-per-page control. The design places these on the PAGE HEADER row,
 * right-aligned beside the audit-log and Add actions (L95-111) — not above the search field.
 * Exported separately so the page can put them there; `ConfigListToolbar` renders only the
 * search + filters row (L115-122).
 */
export const ConfigListViewControls = ({
	view,
	onViewChange,
	pageSize,
	onPageSizeChange,
	pageSizeOptions,
	showPageSize = true,
	extraControls,
}: ConfigListViewControlsProps) => (
	<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
		{/* Segmented view control (L95-103). */}
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 0.5,
				// Design L95: the segmented track sits on --surface-2, which this app exposes as the
				// `surfaceTwo` role (SIMS-8458 AC11). MUI's action.hover is a generic grey, not that token.
				backgroundColor: (theme) => theme.palette.surfaceTwo.main,
				borderRadius: "0.5rem",
				padding: "0.25rem",
			}}
		>
			{VIEWS.map((v) => {
				const active = view === v.value;
				return (
					<Box
						key={v.value}
						component="button"
						type="button"
						aria-pressed={active}
						onClick={() => onViewChange(v.value)}
						sx={{
							display: "flex",
							alignItems: "center",
							gap: "0.375rem",
							height: "2rem",
							padding: "0 0.75rem",
							border: "none",
							borderRadius: "0.375rem",
							cursor: "pointer",
							fontSize: "0.8125rem",
							fontWeight: 600,
							backgroundColor: active ? "background.paper" : "transparent",
							color: active ? "primary.main" : "text.secondary",
							boxShadow: active ? 1 : 0,
						}}
					>
						<v.Icon sx={{ fontSize: "1.125rem" }} />
						{v.label}
					</Box>
				);
			})}
			{extraControls}
		</Box>

		{/* FR-10: rows-per-page governs both views. Design v1 put it beside the view toggle; the
		    v2 re-issue moved it into the pager, so a screen can opt out here. */}
		{showPageSize && (
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<Box
					component="span"
					sx={{ fontSize: "0.75rem", color: "text.secondary" }}
				>
					Show
				</Box>
				<Box
					component="select"
					aria-label="Rows per page"
					value={pageSize}
					onChange={(e: ChangeEvent<HTMLSelectElement>) =>
						onPageSizeChange(e.target.value)
					}
					sx={{
						height: "2.5rem",
						border: "1px solid",
						borderColor: "divider",
						borderRadius: "0.5rem",
						padding: "0 0.75rem",
						fontSize: "0.8125rem",
						color: "text.primary",
						backgroundColor: "background.paper",
					}}
				>
					{pageSizeOptions.map((o) => (
						<option key={o} value={o}>
							{o === "all" ? "All" : o}
						</option>
					))}
				</Box>
			</Box>
		)}
	</Box>
);

const ConfigListToolbar = ({
	query,
	onQueryChange,
	searchPlaceholder,
	status,
	onStatusChange,
	statusOptions,
	categoryLabel,
	category,
	onCategoryChange,
	categoryOptions,
	categoryVariant = "select",
	allCategoriesLabel,
	filters,
}: ConfigListToolbarProps) => (
	// Search + filters only (L115-122). The view toggle and rows-per-page live on the page header
	// row — see ConfigListViewControls above.
	<Box
		sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}
	>
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				backgroundColor: "background.paper",
				border: "1px solid",
				borderColor: "divider",
				borderRadius: "0.5rem",
				height: "2.5rem",
				padding: "0 0.75rem",
				width: "23.75rem", // 380px
				maxWidth: "100%",
			}}
		>
			<SearchIcon sx={{ fontSize: "1.25rem", color: "text.secondary" }} />
			<InputBase
				value={query}
				onChange={(e) => onQueryChange(e.target.value)}
				placeholder={searchPlaceholder}
				inputProps={{ "aria-label": "Search" }}
				sx={{ flex: 1, fontSize: "0.875rem" }}
			/>
		</Box>

		{/* Status chips (L119-122) — pills, not a select. */}
		{statusOptions.length > 0 && (
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				{statusOptions.map((o) => (
					<FilterChip
						key={o.value}
						label={o.label}
						active={status === o.value}
						onClick={() => onStatusChange(o.value)}
					/>
				))}
			</Box>
		)}

		{/* Category filter (L124-132). Every category is offered, retired ones included: hiding
		    a retired one would make the payers carrying it unreachable (AC5). */}
		{categoryVariant === "chips" &&
			categoryOptions &&
			categoryOptions.length > 0 &&
			onCategoryChange && (
				<Box
					role="group"
					aria-label={categoryLabel ?? "Category"}
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						flexWrap: "wrap",
					}}
				>
					{[
						{ value: "all", label: allCategoriesLabel ?? "All" },
						...categoryOptions,
					].map((o) => (
						<FilterChip
							key={o.value}
							label={o.label}
							active={(category ?? "all") === o.value}
							onClick={() => onCategoryChange(o.value)}
						/>
					))}
				</Box>
			)}

		{categoryVariant !== "chips" &&
			categoryOptions &&
			categoryOptions.length > 0 &&
			onCategoryChange && (
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Box
						component="label"
						htmlFor="config-category-filter"
						sx={{
							fontSize: "0.75rem",
							fontWeight: 500,
							color: "text.secondary",
						}}
					>
						{categoryLabel ?? "Category"}
					</Box>
					<Box
						component="select"
						id="config-category-filter"
						value={category}
						onChange={(e: ChangeEvent<HTMLSelectElement>) =>
							onCategoryChange(e.target.value)
						}
						sx={{
							height: "2rem",
							border: "1px solid",
							borderColor: "divider",
							borderRadius: "0.5rem",
							padding: "0 0.5rem",
							fontSize: "0.8125rem",
							backgroundColor: "background.paper",
							color: "text.primary",
						}}
					>
						<option value="all">All categories</option>
						{categoryOptions.map((o) => (
							<option key={o.value} value={o.value}>
								{o.label}
							</option>
						))}
					</Box>
				</Box>
			)}

		{filters}
	</Box>
);

export default ConfigListToolbar;
